/**
 * HelpLegendTab — color/dot legend reference (Day 60c).
 *
 * Re-authored from the dot semantics defined in DetailPanel.tsx:50-55
 * (statusColor function). The four states correspond to the engine's
 * profitability output flags (no_price_data / is_profitable / meets_min_roi)
 * resolved against the operator's `min_roi_pct` business param.
 *
 * Tailwind classes match what DetailPanel uses (`text-ink-muted`, `text-success`,
 * `text-warning`, `text-danger`) — single source of truth across the app.
 */
import type { JSX } from 'react';
import clsx from 'clsx';

interface LegendRow {
  /** Tailwind text color class — same one DetailPanel.tsx:51-54 applies. */
  colorClass: string;
  /** Short label (matches the operator's mental model). */
  label: string;
  /** One-line description of when the state applies. */
  description: string;
}

const LEGEND: ReadonlyArray<LegendRow> = [
  {
    colorClass: 'text-ink-muted',
    label: 'No price data',
    description: 'Algopix returned no marketplace price for this product on this channel.',
  },
  {
    colorClass: 'text-danger',
    label: 'No profit',
    description: 'Calculated, ROI ≤ 0. Selling would lose money after costs and fees.',
  },
  {
    colorClass: 'text-warning',
    label: 'Profitable',
    description: 'ROI > 0 but below the min_roi_pct threshold.',
  },
  {
    colorClass: 'text-success',
    label: 'Meets ROI',
    description: 'ROI ≥ the min_roi_pct threshold. Cleared for buying decisions.',
  },
];

export default function HelpLegendTab(): JSX.Element {
  return (
    <div className="px-4 py-3 space-y-3">
      <p className="text-2xs text-ink-muted leading-relaxed">
        Color coding used across Buying Session, Detail Panel, and product grids.
      </p>
      <ul className="space-y-2.5">
        {LEGEND.map((row) => (
          <li key={row.label} className="flex items-start gap-3 text-xs">
            <span
              aria-hidden="true"
              className={clsx(
                'inline-block w-2.5 h-2.5 rounded-full bg-current flex-shrink-0 mt-1',
                row.colorClass
              )}
            />
            <div className="flex-1 min-w-0">
              <div className={clsx('font-medium leading-snug', row.colorClass)}>{row.label}</div>
              <div className="text-2xs text-ink-muted mt-0.5 leading-snug">{row.description}</div>
            </div>
          </li>
        ))}
      </ul>
      <p className="text-2xs text-ink-muted leading-relaxed pt-1 border-t border-border-subtle">
        ROI threshold lives at{' '}
        <span className="text-ink-secondary">Settings → Business Defaults → min_roi_pct</span>.
        Default 20%.
      </p>
      {/* FN-4 P-2: clarify the two ROI bases the app surfaces. */}
      <p className="text-2xs text-ink-muted leading-relaxed pt-1">
        The buying grid&rsquo;s <span className="text-ink-secondary">ROI% (landed)</span> divides
        profit by fully-loaded landed cost (engine reorderability). The PO Impact toolbar&rsquo;s{' '}
        <span className="text-ink-secondary">ROI% (outlay)</span> divides by raw outlay
        (cash-on-cash on what you actually pay) &mdash; both correct, different questions.
      </p>
    </div>
  );
}
